import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  BookOpen, Trophy, Star, Plus, Medal, TrendingUp,
  Clock, CheckCircle2, Loader2, Play, Gift, Users, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['General', 'Clinical', 'Safety', 'Communication', 'Compliance', 'Leadership'];
const REWARDS_CATALOG = [
  { name: 'Extra Day Off', points: 500, icon: '🏖' },
  { name: 'Gift Voucher ($25)', points: 300, icon: '🎁' },
  { name: 'Training Certificate', points: 200, icon: '📜' },
  { name: 'Team Lunch', points: 150, icon: '🍕' },
  { name: 'Priority Shift Selection', points: 100, icon: '⭐' },
];

export default function LearningRewards() {
  const { user } = useAuth();
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseDialog, setCourseDialog] = useState(false);
  const [viewCourse, setViewCourse] = useState(null);

  const [courseForm, setCourseForm] = useState({
    title: '', description: '', category: 'general', content_type: 'article',
    content: '', duration_minutes: 30, points_reward: 10, mandatory: false
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [cRes, rRes, lRes] = await Promise.all([
      axios.get(`${API_URL}/api/learning/courses`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/rewards/my`, { withCredentials: true }).catch(() => ({ data: { total_points: 0, available_points: 0, history: [] } })),
      axios.get(`${API_URL}/api/rewards/leaderboard`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setCourses(cRes.data);
    setRewards(rRes.data);
    setLeaderboard(lRes.data);
    setLoading(false);
  };

  const createCourse = async () => {
    if (!courseForm.title) return toast.error('Title required');
    try {
      await axios.post(`${API_URL}/api/learning/courses`, courseForm, { withCredentials: true });
      toast.success('Course created');
      setCourseDialog(false);
      setCourseForm({ title: '', description: '', category: 'general', content_type: 'article', content: '', duration_minutes: 30, points_reward: 10, mandatory: false });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const completeCourse = async (courseId) => {
    try {
      await axios.post(`${API_URL}/api/learning/courses/${courseId}/progress`, { progress_pct: 100 }, { withCredentials: true });
      toast.success('Course completed! Points earned.');
      setViewCourse(null);
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const redeemReward = async (reward) => {
    if (!rewards || rewards.available_points < reward.points) return toast.error('Insufficient points');
    try {
      await axios.post(`${API_URL}/api/rewards/redeem`, { reward_name: reward.name, points_cost: reward.points }, { withCredentials: true });
      toast.success(`Redeemed: ${reward.name}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Redemption failed'); }
  };

  const tabs = [
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'rewards', label: 'Rewards', icon: Trophy },
    { id: 'leaderboard', label: 'Leaderboard', icon: Medal },
  ];

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="learning-rewards-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <BookOpen className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Learning & Rewards
            </h1>
            <p className="text-muted-foreground mt-1">Develop skills and earn rewards</p>
          </div>
          {rewards && (
            <div className="text-right">
              <p className="text-2xl font-bold text-[#00A6A0]">{rewards.available_points}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Points Available</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 border-b border-border pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}
              onClick={() => setActiveTab(t.id)} data-testid={`lr-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {/* COURSES TAB */}
        {activeTab === 'courses' && (
          <div className="space-y-4">
            {isAdmin && (
              <Button className="btn-primary" onClick={() => setCourseDialog(true)} data-testid="new-course-btn">
                <Plus className="w-4 h-4 mr-2" />Add Course
              </Button>
            )}

            {courses.length === 0 ? (
              <Card className="glass-card border-border">
                <CardContent className="p-8 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No courses available yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map(c => (
                  <Card key={c.id} className="glass-card border-border hover:border-[#00A6A0]/30 transition-colors cursor-pointer"
                    onClick={() => setViewCourse(c)} data-testid={`course-${c.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0] uppercase">{c.category}</span>
                        {c.mandatory && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Required</span>}
                      </div>
                      <h3 className="text-sm font-medium text-foreground mb-1">{c.title}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{c.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{c.duration_minutes}m</span>
                          <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" />{c.points_reward}pts</span>
                        </div>
                        {c.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <div className="w-8 h-1.5 rounded-full bg-muted">
                            <div className="h-full rounded-full bg-[#00A6A0]" style={{ width: `${c.progress_pct}%` }} />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REWARDS TAB */}
        {activeTab === 'rewards' && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#00A6A0]">{rewards?.total_points || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Total Earned</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <Star className="w-8 h-8 text-[#00A6A0] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{rewards?.available_points || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Available</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <Gift className="w-8 h-8 text-[#FF6F61] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#FF6F61]">{rewards?.redeemed_points || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Redeemed</p>
                </CardContent>
              </Card>
            </div>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Gift className="w-5 h-5 text-[#FF6F61]" />Rewards Catalog
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {REWARDS_CATALOG.map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-muted rounded-xl border border-border">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{r.icon}</span>
                        <div>
                          <p className="text-sm text-foreground font-medium">{r.name}</p>
                          <p className="text-xs text-amber-400">{r.points} points</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline"
                        className={`text-xs ${(rewards?.available_points || 0) >= r.points ? 'border-[#00A6A0]/30 text-[#00A6A0]' : 'border-border text-muted-foreground'}`}
                        disabled={(rewards?.available_points || 0) < r.points}
                        onClick={() => redeemReward(r)}
                        data-testid={`redeem-${i}`}>
                        Redeem
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* LEADERBOARD TAB */}
        {activeTab === 'leaderboard' && (
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Medal className="w-5 h-5 text-amber-400" />Points Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">No leaderboard data yet</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry, i) => (
                    <div key={entry.user_id} className={`flex items-center justify-between p-3 rounded-lg border ${i < 3 ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted border-border'}`}
                      data-testid={`leaderboard-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-gray-300/20 text-gray-300' : i === 2 ? 'bg-orange-600/20 text-orange-400' : 'bg-muted text-muted-foreground'}`}>
                          {i < 3 ? <Crown className="w-4 h-4" /> : entry.rank}
                        </div>
                        <div>
                          <p className="text-sm text-foreground font-medium">{entry.user_name}</p>
                          <p className="text-[10px] text-muted-foreground">Rank #{entry.rank}</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-[#00A6A0]">{entry.total}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Course Detail Dialog */}
        <Dialog open={!!viewCourse} onOpenChange={() => setViewCourse(null)}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-foreground">{viewCourse?.title}</DialogTitle>
            </DialogHeader>
            {viewCourse && (
              <div className="space-y-4">
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span className="px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0] capitalize">{viewCourse.category}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{viewCourse.duration_minutes} min</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" />{viewCourse.points_reward} pts</span>
                </div>
                <p className="text-sm text-muted-foreground">{viewCourse.description}</p>
                {viewCourse.content && (
                  <div className="p-4 bg-muted rounded-lg border border-border max-h-[300px] overflow-y-auto">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{viewCourse.content}</p>
                  </div>
                )}
                {viewCourse.completed ? (
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <CheckCircle2 className="w-5 h-5" />Course completed
                  </div>
                ) : (
                  <Button className="w-full btn-primary" onClick={() => completeCourse(viewCourse.id)} data-testid="complete-course-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" />Mark as Complete
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Course Dialog */}
        <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-foreground">Create Course</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div><label className="text-xs text-muted-foreground block mb-1">Title</label>
                <Input value={courseForm.title} onChange={e => setCourseForm(f => ({ ...f, title: e.target.value }))}
                  className="bg-muted border-border text-foreground" data-testid="course-title-input" /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Description</label>
                <textarea value={courseForm.description} onChange={e => setCourseForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground block mb-1">Category</label>
                  <select value={courseForm.category} onChange={e => setCourseForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10">
                    {CATEGORIES.map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
                  </select>
                </div>
                <div><label className="text-xs text-muted-foreground block mb-1">Duration (min)</label>
                  <Input type="number" value={courseForm.duration_minutes} onChange={e => setCourseForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 30 }))}
                    className="bg-muted border-border text-foreground" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground block mb-1">Points Reward</label>
                  <Input type="number" value={courseForm.points_reward} onChange={e => setCourseForm(f => ({ ...f, points_reward: parseInt(e.target.value) || 10 }))}
                    className="bg-muted border-border text-foreground" /></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={courseForm.mandatory} onChange={e => setCourseForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded" />
                    <span className="text-sm text-muted-foreground">Mandatory</span>
                  </label>
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground block mb-1">Course Content</label>
                <textarea value={courseForm.content} onChange={e => setCourseForm(f => ({ ...f, content: e.target.value }))}
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[120px] resize-none" placeholder="Write course material here..." /></div>
              <Button className="w-full btn-primary" onClick={createCourse} data-testid="submit-course-btn">Publish Course</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
